import React, { useState } from "react";
import api from "../../../../api";
import RubricTreeNode, { removeFromTree } from "./RubricTreeNode";
import { describeApiError } from "./apiError";

const SearchTab = () => {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [expandedId, setExpandedId] = useState(null);
  const [rubricTrees, setRubricTrees] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [detailError, setDetailError] = useState({});
  const [deletingIds, setDeletingIds] = useState({});

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setSearchError("");
    setRows([]);
    setExpandedId(null);
    setSearched(true);

    try {
      const res = await api.get(
        `/api/rubrics/search?keyword=${encodeURIComponent(query)}`
      );

      const formatted = res.data.map((item) => ({
        id: item.id,
        book: item.book,
        rubric: item.rubric,
        remedies: item.remedies.map((r) => ({
          name: r.abbrev, // e.g. Abies-c.
          fullName: r.remedy, // e.g. Abies Canadensis
          weight: r.weight,
        })),
      }));

      setRows(formatted);
    } catch (err) {
      console.error(err);
      setSearchError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRubricTree = async (id) => {
    setDetailLoading((prev) => ({ ...prev, [id]: true }));
    setDetailError((prev) => ({ ...prev, [id]: "" }));

    try {
      const res = await api.get(`/api/repertory/rubric/${id}`);
      setRubricTrees((prev) => ({ ...prev, [id]: res.data }));
    } catch (err) {
      console.error(err);
      setDetailError((prev) => ({
        ...prev,
        [id]: "Failed to load rubric details.",
      }));
    } finally {
      setDetailLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const toggleExpand = (row) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(row.id);
    if (!rubricTrees[row.id]) {
      fetchRubricTree(row.id);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Delete this rubric? This will also remove any sub-rubrics under it and cannot be undone."
      )
    ) {
      return;
    }

    setDeletingIds((prev) => ({ ...prev, [id]: true }));

    try {
      await api.delete(`/api/repertory/rubric/${id}`);

      setRows((prev) => prev.filter((row) => String(row.id) !== String(id)));
      setRubricTrees((prev) => {
        const next = {};
        Object.keys(prev).forEach((key) => {
          if (String(key) === String(id)) return; // drop the deleted node's own cached tree
          next[key] = removeFromTree(prev[key], id);
        });
        return next;
      });

      if (String(expandedId) === String(id)) setExpandedId(null);
    } catch (err) {
      console.error(err);
      alert(describeApiError(err, "Failed to delete rubric."));
    } finally {
      setDeletingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h5 className="card-title">Search Rubrics</h5>
        <p className="text-muted small mb-3">
          Search across every uploaded chapter for a rubric by keyword —
          mirrors the search customers see, with view and delete added for
          admin use.
        </p>

        <div className="input-group mb-3" style={{ maxWidth: "480px" }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search rubric (e.g. fever)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            className="btn btn-primary"
            onClick={handleSearch}
            disabled={loading || !query.trim()}
          >
            {loading ? "Searching…" : "Search"}
          </button>
        </div>

        {searchError && <div className="alert alert-danger">{searchError}</div>}

        {loading && (
          <div className="text-center py-4">
            <span className="spinner-border" role="status" />
          </div>
        )}

        {!loading && searched && !searchError && rows.length === 0 && (
          <p className="text-muted">
            No rubrics found for &ldquo;{query}&rdquo;.
          </p>
        )}

        {!loading && rows.length > 0 && (
          <>
            <p className="text-muted small">
              <strong>{rows.length}</strong> rubrics match &ldquo;{query}&rdquo;
            </p>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Rubric</th>
                    <th>Remedies</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr>
                        <td
                          role="button"
                          onClick={() => toggleExpand(row)}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="me-2">
                            {expandedId === row.id ? "▼" : "▶"}
                          </span>
                          <strong>{row.rubric}</strong>
                          <div className="text-muted small ms-4">
                            Book: {row.book}
                          </div>
                        </td>
                        <td>
                          {row.remedies.map((r, i) => (
                            <span
                              key={i}
                              className={r.weight >= 2 ? "fw-bold" : ""}
                            >
                              {r.name}
                              {i < row.remedies.length - 1 && ", "}
                            </span>
                          ))}
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(row.id)}
                            disabled={!!deletingIds[row.id]}
                          >
                            {deletingIds[row.id] ? "Deleting…" : "Delete"}
                          </button>
                        </td>
                      </tr>
                      {expandedId === row.id && (
                        <tr>
                          <td colSpan={3} className="bg-light">
                            {detailLoading[row.id] && (
                              <div className="text-muted small">
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                />
                                Loading rubric tree…
                              </div>
                            )}
                            {detailError[row.id] && (
                              <div className="text-danger small">
                                {detailError[row.id]}{" "}
                                <button
                                  className="btn btn-sm btn-link p-0"
                                  onClick={() => fetchRubricTree(row.id)}
                                >
                                  Retry
                                </button>
                              </div>
                            )}
                            {rubricTrees[row.id] && (
                              <RubricTreeNode
                                node={rubricTrees[row.id]}
                                depth={0}
                                deletingIds={deletingIds}
                                onDelete={handleDelete}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchTab;
